import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  withRouter,
  Switch as RouterSwitch,
  useHistory,
  useLocation
} from 'react-router-dom'
import {
  Button,
  Modal,
  Table,
  Tag,
  Input,
  InputNumber,
  Select,
  Switch,
  Space,
  Empty,
  Popconfirm,
  Row,
  Col
} from 'antd'
import PageLoader from '../../../components/Layouts/PageLoader'
import PrivateRoute from '../../../hocs/PrivateRoute'
import PaginationContainer from '../../../components/Forms/Pagination'
import {
  getRequest,
  postRequest,
  patchRequest,
  deleteRequest,
  formatUrl,
  dutyDealersUrl,
  customDutyBodyTypesUrl,
  successNotification,
  errorNotification
} from '../../../config'

const { Option } = Select

const BLANK = {
  phone: '',
  companyName: '',
  contactName: '',
  brand: '',
  discountPercent: 0,
  isActive: true,
  notes: '',
  bodyTypePrices: []
}

const DealersList = () => {
  const [loading, setLoading] = useState(false)
  const [dealers, setDealers] = useState([])
  const [bodyTypes, setBodyTypes] = useState([])
  const [meta, setMeta] = useState({ previous: null, next: null, count: 0 })
  const [editing, setEditing] = useState(null) // record being edited, or {} for new
  const [form, setForm] = useState(BLANK)
  const [saving, setSaving] = useState(false)

  const getDealers = async (url = null) => {
    const response = await getRequest(
      url ? formatUrl(url) : dutyDealersUrl,
      'dealers',
      setLoading
    )
    if (response.success) {
      setDealers(response.data.results || response.data)
      setMeta({ ...response.data })
    }
  }

  useEffect(() => {
    getDealers()
    // Body types power the per-body-type override editor.
    const loadBodyTypes = async () => {
      const res = await getRequest(
        `${customDutyBodyTypesUrl}/all`,
        'body types',
        () => {}
      )
      if (res.success) {
        setBodyTypes((res.data || []).map(b => ({ id: b.id, name: b.name })))
      }
    }
    loadBodyTypes()
    // eslint-disable-next-line
  }, [])

  const openNew = () => {
    setForm(BLANK)
    setEditing({})
  }

  // Arriving from "Register as dealer" on the follow-up results (spec: duty-follow-up-messages):
  // open the create form with the customer's phone already filled in, then drop the parameters so a
  // refresh does not open it again.
  const location = useLocation()
  const history = useHistory()
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('new') === '1') {
      setForm({ ...BLANK, phone: params.get('phone') || '' })
      setEditing({})
      history.replace(location.pathname)
    }
    // eslint-disable-next-line
  }, [])

  const openEdit = record => {
    setForm({
      phone: record.phone || '',
      companyName: record.companyName || '',
      contactName: record.contactName || '',
      brand: record.brand || '',
      discountPercent: record.discountPercent ? Number(record.discountPercent) : 0,
      isActive: record.isActive,
      notes: record.notes || '',
      bodyTypePrices: (record.bodyTypePrices || []).map(p => ({
        bodyType: p.bodyType,
        servicePrice: Number(p.servicePrice)
      }))
    })
    setEditing(record)
  }

  const setField = key => value => setForm(f => ({ ...f, [key]: value }))

  const addOverride = () =>
    setForm(f => ({
      ...f,
      bodyTypePrices: [...f.bodyTypePrices, { bodyType: undefined, servicePrice: 0 }]
    }))

  const setOverride = (idx, key, value) =>
    setForm(f => {
      const rows = [...f.bodyTypePrices]
      rows[idx] = { ...rows[idx], [key]: value }
      return { ...f, bodyTypePrices: rows }
    })

  const removeOverride = idx =>
    setForm(f => ({
      ...f,
      bodyTypePrices: f.bodyTypePrices.filter((_, i) => i !== idx)
    }))

  const handleSave = async () => {
    if (!form.phone || !form.companyName) {
      errorNotification('Phone and company name are required')
      return
    }
    if (Number(form.discountPercent) < 0 || Number(form.discountPercent) > 100) {
      errorNotification('Discount percent must be between 0 and 100')
      return
    }
    // Drop incomplete override rows so we never send a half-filled entry.
    const overrides = form.bodyTypePrices.filter(
      p => p.bodyType && p.servicePrice !== null && p.servicePrice !== undefined
    )
    const payload = { ...form, bodyTypePrices: overrides }
    const isNew = !editing.id
    const res = isNew
      ? await postRequest(dutyDealersUrl + '/', payload, 'dealer', setSaving)
      : await patchRequest(
          `${dutyDealersUrl}/${editing.id}/`,
          payload,
          'dealer',
          setSaving
        )
    if (res.success) {
      successNotification(isNew ? 'Dealer added' : 'Dealer updated')
      setEditing(null)
      getDealers()
    }
  }

  const handleDelete = async record => {
    const res = await deleteRequest(
      `${dutyDealersUrl}/${record.id}/`,
      'dealer',
      setLoading
    )
    if (res.success) {
      successNotification('Dealer removed')
      getDealers()
    }
  }

  const bodyTypeName = id => (bodyTypes.find(b => b.id === id) || {}).name || id

  // Table `render` props return JSX but are cell renderers, not components — silence the
  // display-name rule for them.
  /* eslint-disable react/display-name */
  const columns = [
    {
      title: 'Company',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (name, record) => (
        <span>
          <b>{name}</b>
          {!record.isActive && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              Inactive
            </Tag>
          )}
        </span>
      )
    },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    {
      title: 'Discount',
      dataIndex: 'discountPercent',
      key: 'discountPercent',
      render: pct => `${Number(pct || 0).toFixed(2)}%`
    },
    {
      title: 'Overrides',
      key: 'overrides',
      render: (_, record) => {
        const rows = record.bodyTypePrices || []
        if (!rows.length) return <span style={{ color: '#aaa' }}>—</span>
        return (
          <Space size={[0, 4]} wrap>
            {rows.map(p => (
              <Tag key={p.id || p.bodyType}>
                {bodyTypeName(p.bodyType)}: GHS {Number(p.servicePrice).toFixed(2)}
              </Tag>
            ))}
          </Space>
        )
      }
    },
    { title: 'Contact', dataIndex: 'contactName', key: 'contactName' },
    {
      title: 'Action',
      key: 'action',
      width: 170,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Remove this dealer?"
            onConfirm={() => handleDelete(record)}
            okText="Remove"
            cancelText="Cancel"
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]
  /* eslint-enable react/display-name */

  return (
    <PageLoader className="container" loading={loading}>
      <div className="card mb-5rem">
        <div className="card-body">
          <h3 style={{ marginBottom: 16 }}>
            Dealer Accounts {meta && `(${meta.count || dealers.length})`}
          </h3>
          <div style={{ marginBottom: 16 }}>
            <Space>
              <Button type="primary" onClick={openNew}>
                + Add Dealer
              </Button>
              <Button onClick={() => getDealers()}>Refresh</Button>
            </Space>
            <p style={{ marginTop: 8, color: '#888' }}>
              B2B dealers get discounted duty-service pricing, matched by their WhatsApp
              number. Pricing is a flat percentage off the body-type fee, with an optional
              fixed price per body type. A dealer&apos;s rate replaces the older-car discount;
              100% (or a 0 override) makes that check free.
            </p>
          </div>

          <Table
            rowKey="id"
            dataSource={dealers}
            columns={columns}
            pagination={false}
            locale={{ emptyText: <Empty description="No dealers yet" /> }}
          />
          {dealers.length ? (
            <PaginationContainer meta={meta} getData={url => getDealers(url)} />
          ) : null}
        </div>
      </div>

      <Modal
        title={editing?.id ? `Edit dealer — ${editing.companyName}` : 'Add dealer'}
        centered
        open={!!editing}
        onOk={handleSave}
        onCancel={() => setEditing(null)}
        okText="Save"
        confirmLoading={saving}
        width={640}
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12}>
            <label>WhatsApp / MoMo number</label>
            <Input
              placeholder="0244123456"
              value={form.phone}
              onChange={e => setField('phone')(e.target.value)}
            />
          </Col>
          <Col xs={24} md={12}>
            <label>Company name</label>
            <Input
              placeholder="Acme Motors"
              value={form.companyName}
              onChange={e => setField('companyName')(e.target.value)}
            />
          </Col>
          <Col xs={24} md={12}>
            <label>Contact name</label>
            <Input
              value={form.contactName}
              onChange={e => setField('contactName')(e.target.value)}
            />
          </Col>
          <Col xs={24} md={12}>
            <label>Brand (optional)</label>
            <Input
              value={form.brand}
              onChange={e => setField('brand')(e.target.value)}
            />
          </Col>
          <Col xs={24} md={12}>
            <label>Discount percent</label>
            <InputNumber
              min={0}
              max={100}
              precision={2}
              style={{ width: '100%' }}
              value={form.discountPercent}
              onChange={setField('discountPercent')}
            />
          </Col>
          <Col xs={24} md={12}>
            <label style={{ display: 'block' }}>Active</label>
            <Switch checked={form.isActive} onChange={setField('isActive')} />
          </Col>
          <Col xs={24}>
            <label>Notes</label>
            <Input.TextArea
              rows={2}
              value={form.notes}
              onChange={e => setField('notes')(e.target.value)}
            />
          </Col>
        </Row>

        <div style={{ marginTop: 16 }}>
          <label style={{ fontWeight: 600 }}>
            Per-body-type price overrides (optional)
          </label>
          <p style={{ color: '#888', fontSize: 12, margin: '4px 0 8px' }}>
            A fixed fee for a specific body type — overrides the percentage for that body
            type only.
          </p>
          {form.bodyTypePrices.map((row, idx) => (
            <Row gutter={[8, 8]} key={idx} style={{ marginBottom: 8 }}>
              <Col xs={12}>
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  placeholder="Body type"
                  style={{ width: '100%' }}
                  value={row.bodyType}
                  onChange={v => setOverride(idx, 'bodyType', v)}
                >
                  {bodyTypes.map(b => (
                    <Option key={b.id} value={b.id}>
                      {b.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={8}>
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="Price (GHS)"
                  value={row.servicePrice}
                  onChange={v => setOverride(idx, 'servicePrice', v)}
                />
              </Col>
              <Col xs={4}>
                <Button danger onClick={() => removeOverride(idx)}>
                  ✕
                </Button>
              </Col>
            </Row>
          ))}
          <Button onClick={addOverride} size="small">
            + Add override
          </Button>
        </div>
      </Modal>
    </PageLoader>
  )
}

const DealersRoutes = props => (
  <RouterSwitch>
    <PrivateRoute exact path={props.match.path} component={DealersList} />
  </RouterSwitch>
)

DealersRoutes.propTypes = {
  match: PropTypes.object
}

export default withRouter(DealersRoutes)
